import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { parseAxiosError } from '@/api/api-axios';
import { QUERY_KEY, useGetTelemetryStatusQuery } from '@/api/api-hooks';
import { VersionStatus, VersionStatusType } from '@/api/types/node';
import { CodebaseTelemetry, TelemetryStatusResponse } from '@/api/types/telemetry';
import { clearProgressHighWaterMark } from '@/components/StatusIndicators/InlineProgress';

const TELEMETRY_POLL_INTERVAL = 10_000; // 10 seconds

const LIBRARY_QUERY_KEYS: readonly string[] = [QUERY_KEY.getCodebaseCard, QUERY_KEY.getPrimaryAsset];

// Predicate for queryClient.invalidateQueries. Cache key shape is always
// [QUERY_KEY.getXxx, paramsObject]. List-view queries (no id__in) always
// refetch on any change; per-id-batch poller queries only refetch when one
// of their batched ids is in changedIds — avoids cross-talk between
// concurrent poller subscriptions for disjoint id sets.
export function isLibraryQueryAffectedByChanges(
  queryKey: readonly unknown[],
  changedIds: ReadonlySet<string>
): boolean {
  const [keyName, params] = queryKey;
  if (typeof keyName !== 'string' || !LIBRARY_QUERY_KEYS.includes(keyName)) return false;
  if (params && typeof params === 'object' && 'id__in' in params) {
    const idIn = (params as { id__in?: unknown }).id__in;
    if (typeof idIn === 'string') {
      return idIn.split(',').some((id) => changedIds.has(id));
    }
  }
  return true;
}

export type TelemetryIndicatorStatus = 'all_good' | 'error' | 'activity' | 'notification' | 'loading' | 'network_error';

type TransitionListener = (transition: CodebaseTransition) => void;

interface TelemetryContextType {
  data: TelemetryStatusResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  networkErrorMessage: string | null;

  hasActiveOperations: boolean;
  hasErrors: boolean;
  errorCount: number;
  telemetryIndicatorStatus: TelemetryIndicatorStatus;
  summaryText: string;

  sortedCodebases: CodebaseTelemetry[];

  // Subscribe to per-codebase status transitions. Detection lives in the
  // provider (not the consumer) so the lastSeen map survives consumer
  // remounts — e.g. FirstCodebaseCelebrationGate mounted in LayoutDefault,
  // which remounts on every route change. Without provider-level state, a
  // route change between GENERATING and GENERATION_COMPLETE would reset
  // lastSeen, the next observation would have fromStatus=null, and the
  // celebration's null-filter would suppress it permanently.
  subscribeToTransitions: (listener: TransitionListener) => () => void;
}

// Exported for testing: tests wrap with `<TelemetryContext.Provider value={mock}>`
// rather than mocking `useTelemetry` (same-module mock binding does not intercept
// internal calls from sibling exports like `useCodebaseTelemetry`).
export const TelemetryContext = createContext<TelemetryContextType | null>(null);

export function TelemetryProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { data, error, isError, isLoading } = useGetTelemetryStatusQuery({
    refetchInterval: TELEMETRY_POLL_INTERVAL,
  });

  // Stays true only until the first successful fetch, avoiding unnecessary
  // re-computations from `data` getting a new reference on every poll cycle.
  const isInitialLoad = isLoading && !data;

  // Telemetry polls every 10s and is the canonical "what codebases exist
  // and what state are they in" source. The library tables read from
  // separate caches (getCodebaseCard / getPrimaryAsset) that only refetch
  // when explicitly invalidated. When telemetry observes a *change* (a new
  // codebase id, a removed id, or a status change), invalidate those
  // library caches so the tables stay in sync without page refresh.
  // Skips the very first observation so we don't refetch caches that the
  // page just loaded.
  // Per-codebase status snapshot from the previous tick — drives both the
  // diff that decides which queries to invalidate AND the transition
  // listener fan-out. Encodes unmapped statuses with a distinct sentinel
  // (collapsing them into 'pending' would mask transitions between
  // 'pending' and a future unmapped status that should still trigger a
  // refetch).
  const lastSeenStatusByIdRef = useRef<Map<string, string>>(new Map());
  const hasInitialObservationRef = useRef(false);
  // Per-codebase last-seen *typed* status — drives transition detection.
  // Lives at the provider (mounted once at app root) so it survives
  // consumer remounts on route changes. See
  // TelemetryContextType.subscribeToTransitions.
  const transitionLastSeenRef = useRef<Map<string, VersionStatusType>>(new Map());
  const transitionListenersRef = useRef<Set<TransitionListener>>(new Set());

  const subscribeToTransitions = useCallback((listener: TransitionListener) => {
    transitionListenersRef.current.add(listener);
    return () => {
      transitionListenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!data?.codebases) return;

    // Build the current status map for diffing.
    const currentStatusById = new Map<string, string>();
    for (const cb of data.codebases) {
      currentStatusById.set(cb.id, deriveVersionStatus(cb) ?? '__unmapped__');
    }

    // Compute the set of codebase ids whose status has changed since the
    // last tick — added, status-changed, or removed all count.
    const changedIds = new Set<string>();
    for (const [id, status] of currentStatusById) {
      if (lastSeenStatusByIdRef.current.get(id) !== status) changedIds.add(id);
    }
    for (const id of lastSeenStatusByIdRef.current.keys()) {
      if (!currentStatusById.has(id)) changedIds.add(id);
    }

    // Skip invalidation on the first observation (the page just loaded the
    // data, nothing to refresh). Still run transition detection so initial
    // statuses are captured for the lastSeen map.
    if (hasInitialObservationRef.current && changedIds.size > 0) {
      // Predicate-based invalidation: list queries (no id filter) always
      // refetch when something changed; per-id-batch queries (the poller
      // uses `id__in=<comma-separated>`) only refetch when one of their
      // batched ids is in changedIds. Avoids cross-talk between concurrent
      // poller subscriptions for disjoint id sets.
      queryClient.invalidateQueries({
        predicate: (query) => isLibraryQueryAffectedByChanges(query.queryKey, changedIds),
      });
    }

    // Per-codebase transition detection — fire listeners exactly once per
    // (id, fromStatus → toStatus) pair across the provider's lifetime.
    for (const cb of data.codebases) {
      const newStatus = deriveVersionStatus(cb);
      if (newStatus === null) continue;
      const prev = transitionLastSeenRef.current.get(cb.id) ?? null;
      if (prev !== newStatus) {
        transitionLastSeenRef.current.set(cb.id, newStatus);
        const transition: CodebaseTransition = {
          codebaseId: cb.id,
          codebaseName: cb.name,
          fromStatus: prev,
          toStatus: newStatus,
          observedAt: Date.now(),
        };
        transitionListenersRef.current.forEach((listener) => listener(transition));
      }
    }

    // Clean up per-codebase state for ids that are no longer in telemetry
    // (deleted, archived, etc.). Without this, the high-water Map in
    // InlineProgress and the transition lastSeen Map slowly accumulate
    // stale entries across the session.
    for (const previousId of lastSeenStatusByIdRef.current.keys()) {
      if (!currentStatusById.has(previousId)) {
        clearProgressHighWaterMark(previousId);
        transitionLastSeenRef.current.delete(previousId);
      }
    }

    lastSeenStatusByIdRef.current = currentStatusById;
    hasInitialObservationRef.current = true;
  }, [data, queryClient]);

  const networkErrorMessage = useMemo(() => {
    if (!isError || !error) return null;
    return parseAxiosError(error, 'Failed to fetch telemetry status');
  }, [isError, error]);

  const hasActiveOperations = useMemo(() => {
    if (!data?.codebases) return false;
    return data.codebases.some((cb) => cb.overall_status === 'in_progress' || cb.overall_status === 'pending');
  }, [data]);

  const hasPendingOnly = useMemo(() => {
    if (!data?.codebases) return false;
    const hasPending = data.codebases.some((cb) => cb.overall_status === 'pending');
    const hasInProgress = data.codebases.some((cb) => cb.overall_status === 'in_progress');
    return hasPending && !hasInProgress;
  }, [data]);

  const errorCount = useMemo(() => {
    if (!data?.codebases) return 0;
    return data.codebases.filter((cb) => cb.overall_status === 'error').length;
  }, [data]);

  const hasErrors = errorCount > 0;

  const telemetryIndicatorStatus = useMemo((): TelemetryIndicatorStatus => {
    if (isInitialLoad) return 'loading';
    if (isError) return 'network_error';
    if (hasErrors) return 'error';
    if (hasActiveOperations) return 'activity';
    return 'all_good';
  }, [hasActiveOperations, hasErrors, isError, isInitialLoad]);

  const summaryText = useMemo(() => {
    if (isInitialLoad) return 'Loading...';
    if (isError) return 'Connection error';
    if (hasErrors) return `${errorCount} codebase${errorCount === 1 ? '' : 's'} with errors`;
    if (hasActiveOperations) return hasPendingOnly ? 'Pending operations' : 'Currently updating';
    return 'All systems ready';
  }, [errorCount, hasActiveOperations, hasPendingOnly, hasErrors, isError, isInitialLoad]);

  const sortedCodebases = useMemo((): CodebaseTelemetry[] => {
    if (!data?.codebases) return [];

    const statusPriority: Record<string, number> = {
      error: 0,
      in_progress: 1,
      pending: 2,
      complete: 3,
    };

    return [...data.codebases].sort((a, b) => {
      const aPriority = statusPriority[a.overall_status] ?? 4;
      const bPriority = statusPriority[b.overall_status] ?? 4;
      return aPriority - bPriority;
    });
  }, [data]);

  const value: TelemetryContextType = useMemo(
    () => ({
      data,
      errorCount,
      hasActiveOperations,
      hasErrors,
      isError,
      isLoading,
      networkErrorMessage,
      sortedCodebases,
      subscribeToTransitions,
      summaryText,
      telemetryIndicatorStatus,
    }),
    [
      data,
      errorCount,
      hasActiveOperations,
      hasErrors,
      isError,
      isLoading,
      networkErrorMessage,
      sortedCodebases,
      subscribeToTransitions,
      summaryText,
      telemetryIndicatorStatus,
    ]
  );

  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>;
}

export function useTelemetry() {
  const context = useContext(TelemetryContext);
  if (!context) {
    throw new Error('useTelemetry must be used within a TelemetryProvider');
  }
  return context;
}

// Per-codebase selector — returns the telemetry slice for a single codebase, or
// null when the id is undefined or not present in the latest poll. Reads from
// the existing TelemetryContext (no extra network request); cheap useMemo
// against the codebases array reference, which React Query keeps stable across
// polls when content is unchanged.
export function useCodebaseTelemetry(codebaseId: string | undefined): CodebaseTelemetry | null {
  const { data } = useTelemetry();
  return useMemo(() => {
    if (!codebaseId || !data?.codebases) return null;
    return data.codebases.find((cb) => cb.id === codebaseId) ?? null;
  }, [codebaseId, data?.codebases]);
}

export interface CodebaseTransition {
  codebaseId: string;
  codebaseName: string;
  fromStatus: VersionStatusType | null;
  toStatus: VersionStatusType;
  observedAt: number;
}

// Maps a CodebaseTelemetry row to a VersionStatusType. Precedence:
// error_detail beats everything (most recent meaningful signal), then
// activity='connecting' -> CONNECTING. branches[0].status of
// analytics_ready/context_ready -> GENERATION_COMPLETE (branch state wins
// over background activity). activity='analyzing' -> GENERATING (user
// kicked off generation; honored even if branch still shows 'connected').
// branches[0].status='connected' -> CONNECTED (branch state wins over
// background onboarding/updating so the banner count doesn't flicker).
// activity='onboarding'/'updating' (no branch yet) -> GENERATING. Else
// null. Telemetry response has no is_default flag; we treat branches[0]
// as default.
export function deriveVersionStatus(cb: CodebaseTelemetry): VersionStatusType | null {
  if (cb.error_detail) {
    switch (cb.error_detail.error_type) {
      case 'connection_failed':
        return VersionStatus.CONNECTION_FAILED;
      case 'generation_error':
        return VersionStatus.GENERATION_ERROR;
      case 'insufficient_balance':
        return VersionStatus.INSUFFICIENT_BALANCE;
    }
  }
  if (cb.activity === 'connecting') return VersionStatus.CONNECTING;
  const defaultBranch = cb.branches[0];
  if (defaultBranch?.status === 'analytics_ready' || defaultBranch?.status === 'context_ready') {
    return VersionStatus.GENERATION_COMPLETE;
  }
  // User-initiated generation: honor it even if the branch is still 'connected'.
  if (cb.activity === 'analyzing') {
    return VersionStatus.GENERATING;
  }
  // Branch state is the authoritative readiness signal. A codebase whose branch
  // shows 'connected' is browseable; transient backend activities like
  // 'onboarding' (post-connect metrics) or 'updating' (sync) shouldn't drop it
  // out of the CONNECTED set, which would make the ConnectedReadyBanner count
  // flicker.
  if (defaultBranch?.status === 'connected') {
    return VersionStatus.CONNECTED;
  }
  if (cb.activity === 'updating' || cb.activity === 'onboarding') {
    return VersionStatus.GENERATING;
  }
  return null;
}

// Subscribes to transitions detected by the provider (which runs detection
// once and fans out). Ref-based callback so consumers can pass non-memoized
// callbacks without re-subscribing. First observation has fromStatus=null;
// consumers decide whether to filter those.
export function useStatusTransitions(onTransition: (transition: CodebaseTransition) => void): void {
  const { subscribeToTransitions } = useTelemetry();
  const onTransitionRef = useRef(onTransition);
  onTransitionRef.current = onTransition;

  useEffect(() => {
    return subscribeToTransitions((transition) => onTransitionRef.current(transition));
  }, [subscribeToTransitions]);
}
